import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { actions as homeActions } from "../../../../actions/home";
import { OverlayPreloaderAbsolute } from "../../../common/preloader";
import { ReactComponent as EmptyIcon } from "../../../../media/icons/new-empty-listing.svg";
import FeedTypeChips from "../feedTypeChips";
import useDeepLinkedType from "../feedTypeChips/useDeepLinkedType";
import FeedCard from "../feedCard";
import SuggestedCommunities from "../suggestedCommunities";
import { feedReturnState } from "../../../../helpers/feedReturn";
import styles from "./feedStream.module.css";

// The empty state's words by what the list was asked for.
const EMPTY_TEXT_KEYS = {
  saved: { title: "dashboard_feed_empty_saved_title", body: "dashboard_feed_empty_saved_body" },
  search: { title: "dashboard_feed_empty_search_title", body: "dashboard_feed_empty_search_body" },
  no_results: { title: "dashboard_feed_empty_no_results_title", body: "dashboard_feed_empty_no_results_body" }
};

/**
 * The My Feed v2 stream (GROWTH-7129): new/earlier dividers + cards + cursor "Show older", shared between the Feed page (`mode="feed"`, GET /api-v2/feed) and the
 * Saved page's tab (`mode="saved"`, GET /api-v2/feed/saved, GROWTH-7140). Saved has no
 * last_seen_at/new_count and no dividers — is_new is always false there.
 *
 * Every load is cancelled when the type filter changes or the page is left (AbortController), so
 * a slow response for the previous filter can't replace or extend the current one.
 *
 * The type filter (GROWTH-7573): the Feed page owns it — it lives in the page's sidebar — and
 * passes `type` down; the stream then renders no chips of its own. Without a `type` prop (the
 * Saved page) the stream keeps its own filter and its own chip row, and on `mode="feed"` seeds it
 * from a `?type=` in the URL (useDeepLinkedType). The greeting header moved to the page as well.
 *
 * `communityId`/`onSelectCommunity` are the My Feed sidebar's scope (GROWTH-7130). The Saved
 * page's `<FeedStream mode="saved">` call never passes them, and `getSavedFeed` doesn't accept
 * a `communityId` param even if it were passed — GET /api-v2/feed/saved answers 400 for
 * `community_id` (a saved listing was never matched through a follow).
 *
 * @param {Object} props
 * @param {"feed"|"saved"} props.mode
 * @param {number|null} [props.communityId] the sidebar's selected community; null/omitted = "All communities"
 * @param {Function} [props.onSelectCommunity] (id: number) => void — a card's attribution row sets the same scope
 * @param {string|null} [props.type] the page-owned type filter (null = All); omitted = the stream owns it
 * @param {string|null} [props.q] the header search's text from `/?q=` (GROWTH-7577), feed mode only: the stream then lists
 *   only matching listings, offers no communities to follow when empty, and does not count as a visit to the feed
 */
const FeedStream = ({ mode, communityId = null, onSelectCommunity, type: controlledType, q = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isControlled = controlledType !== undefined;
  const [ownType, setOwnType] = useDeepLinkedType(mode === "feed" && !isControlled);
  const type = isControlled ? controlledType : ownType;

  const { preloaders, errors, feedStream, savedFeed, favoritesChangedAt } = useSelector(state => state.home);
  const { profile } = useSelector(state => state.account);
  const { changedAt: followingChangedAt } = useSelector(state => state.following);
  const moreController = useRef(null);

  const data = mode === "feed" ? feedStream : savedFeed;
  const section = mode === "feed" ? "feedStream" : "savedFeed";
  const moreSection = `${section}More`;
  const loading = preloaders[section];
  const loadingMore = preloaders[moreSection];
  const error = errors[section];
  const moreError = errors[moreSection];

  // GET /api-v2/feed is browser-cached for 60s. After a follow change (GROWTH-7042/7102) or a
  // save/unsave, the latest change time goes on the request as `fresh` so the browser can't
  // answer from that cache — the same param v1's getMyFeed sends; the backend ignores it.
  const fresh = Math.max(followingChangedAt || 0, favoritesChangedAt || 0) || undefined;

  const getFeedStream = useCallback(params => dispatch(homeActions.getFeedStream(params)), [dispatch]);
  const getSavedFeed = useCallback(params => dispatch(homeActions.getSavedFeed(params)), [dispatch]);
  const markFeedSeen = useCallback(() => dispatch(homeActions.markFeedSeen()), [dispatch]);
  const addToFavorite = useCallback(id => dispatch(homeActions.addToFavorite(id)), [dispatch]);
  const removeFavorite = useCallback(id => dispatch(homeActions.removeFavorite(id)), [dispatch]);
  const removeSavedFeedItem = useCallback(id => dispatch(homeActions.removeSavedFeedItem(id)), [dispatch]);
  const sendMessage = useCallback(payload => dispatch(homeActions.sendFeedMessage(payload)), [dispatch]);

  const fetchPage = useCallback(params => (mode === "feed" ? getFeedStream(params) : getSavedFeed(params)), [
    mode,
    getFeedStream,
    getSavedFeed
  ]);

  useEffect(() => {
    // fetchPage omitted: it's getFeedStream/getSavedFeed under a stable useCallback (deps:
    // dispatch, mode), so its identity only changes when `mode` does, already listed below.
    // `fresh` omitted: it's read at load time, a save/unsave alone mustn't reload the list.
    const controller = new AbortController();
    fetchPage({ type, communityId, fresh, q: q || undefined, signal: controller.signal });

    return () => {
      // Filter changed or page left: neither this load nor a "Show older" still in flight for
      // it may land on the list that replaces it.
      controller.abort();
      moreController.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, type, communityId, q]);

  const isSearching = Boolean(q);
  useEffect(() => {
    // Runs once per page visit, independent of the type filter — the backend reads last_seen_at
    // before this write lands, so ordering against the GET above doesn't matter (see
    // FeedController::index_get()/seen_post()). markFeedSeen omitted: stable useCallback (deps: dispatch).
    // A search is not a look at the feed (GROWTH-7577): the visit is recorded when the search is cleared, or on arrival without one.
    if (mode === "feed" && !isSearching) markFeedSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isSearching]);

  const handleToggleBookmark = useCallback(
    async (id, nextBookmarked) => {
      const ok = await (nextBookmarked ? addToFavorite(id) : removeFavorite(id));
      if (ok === true && mode === "saved" && !nextBookmarked) {
        removeSavedFeedItem(id);
      }
      return ok;
    },
    [addToFavorite, removeFavorite, removeSavedFeedItem, mode]
  );

  const handleShowOlder = () => {
    const controller = new AbortController();
    moreController.current = controller;
    fetchPage({
      type,
      communityId,
      cursor: data.nextCursor,
      q: q || undefined,
      append: true,
      fresh,
      signal: controller.signal
    });
  };

  const { items } = data;
  // On the feed, a card's links carry the feed's filter to the listing page, for its "My Feed" breadcrumb (GROWTH-7576),
  // and the header search's text when one is applied (GROWTH-7645). The Saved page's cards carry none: the breadcrumb
  // then opens the feed as it opens by default.
  const cardLinkState = useMemo(() => (mode === "feed" ? feedReturnState({ type, communityId, q }) : undefined), [
    mode,
    type,
    communityId,
    q
  ]);

  // GROWTH-7860: the list ranks no more than `listLimit`, so with more matches than that it ends early. Only the feed's data carries a `listLimit`.
  const cutShort = Number.isFinite(data.listLimit) && data.total > data.listLimit;
  const hasNew = mode === "feed" && items.some(item => item.is_new);
  const hasEarlier = mode === "feed" && items.some(item => !item.is_new);
  const showDividers = hasNew && hasEarlier;

  // Dividers separate "New since your last visit" from "Earlier" — only when both groups are
  // present in what's loaded so far (AC: "appear only when both groups exist"). Items arrive
  // newest-first, so is_new only ever transitions true -> false, once.
  const rows = useMemo(() => {
    const result = [];
    let lastNew = null;
    items.forEach(item => {
      if (showDividers && lastNew !== item.is_new) {
        result.push({ kind: "divider", key: `divider-${item.id}`, isNew: item.is_new });
      }
      result.push({ kind: "card", key: item.id, item });
      lastNew = item.is_new;
    });
    return result;
  }, [items, showDividers]);

  const showEmptyState = !loading && items.length === 0;
  // An unfiltered, empty feed has nothing to narrow: offer communities to follow (GROWTH-7135).
  const showSuggestions = mode === "feed" && !type && communityId === null && !isSearching;
  // The grid waits for the first load to finish: before it, `items` is empty only because nothing
  // has been requested yet, and the grid's own request would be the page's first.
  const [loadedOnce, setLoadedOnce] = useState(false);
  const wasLoading = useRef(false);
  useEffect(() => {
    if (loading) wasLoading.current = true;
    else if (wasLoading.current) setLoadedOnce(true);
  }, [loading]);
  // The grid replaces the illustration only once it has communities to show.
  const [hasSuggestions, setHasSuggestions] = useState(false);

  // A follow from the suggestions grid reloads the feed in place; `fresh` keeps the browser's 60s
  // cache of GET /api-v2/feed from answering with the empty list.
  const handleFollowed = useCallback(() => fetchPage({ type, communityId, fresh: Date.now() }), [fetchPage, type, communityId]);
  const emptyText = EMPTY_TEXT_KEYS[mode === "saved" ? "saved" : isSearching ? "search" : "no_results"];

  return (
    <div className={styles.stream}>
      {isControlled ? null : <FeedTypeChips value={type} onChange={setOwnType} />}

      <div className={styles.list}>
        {loading ? <OverlayPreloaderAbsolute /> : null}

        {error && showEmptyState ? (
          <p className={styles.error} role="alert">
            {error.message}
          </p>
        ) : null}

        {!loading && showEmptyState && !error ? (
          <div className={styles.empty}>
            {showSuggestions && hasSuggestions ? null : <EmptyIcon className={styles.emptyIcon} />}
            <h2 className={styles.emptyTitle}>{t(emptyText.title)}</h2>
            <p className={styles.emptyBody}>{t(emptyText.body)}</p>
            {showSuggestions && loadedOnce ? (
              <SuggestedCommunities onFollowed={handleFollowed} onAvailabilityChange={setHasSuggestions} />
            ) : null}
          </div>
        ) : null}

        {rows.map(row =>
          row.kind === "divider" ? (
            <div className={styles.divider} key={row.key}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerLabel}>{t(row.isNew ? "dashboard_feed_divider_new" : "dashboard_feed_divider_earlier")}</span>
              <span className={styles.dividerLine} />
            </div>
          ) : (
            <FeedCard
              key={row.key}
              item={row.item}
              onToggleBookmark={handleToggleBookmark}
              onSendMessage={sendMessage}
              senderProfile={profile}
              onSelectCommunity={mode === "feed" ? onSelectCommunity : undefined}
              linkState={cardLinkState}
              showReason={mode === "feed"}
            />
          )
        )}
      </div>

      {data.nextCursor ? (
        <div className={styles.showOlder}>
          {moreError ? (
            <p className={styles.error} role="alert">
              {moreError.message}
            </p>
          ) : null}
          <button type="button" className={styles.showOlderButton} onClick={handleShowOlder} disabled={loadingMore}>
            {loadingMore ? (
              <span className={styles.showOlderSpinner} role="status" aria-label={t("dashboard_feed_loading")} />
            ) : (
              t("dashboard_feed_show_older")
            )}
          </button>
        </div>
      ) : cutShort ? (
        <p className={styles.listEnd}>{t("dashboard_feed_list_limit_end", { limit: data.listLimit })}</p>
      ) : null}
    </div>
  );
};

export default FeedStream;
